// Lightweight cart for festival bottle reservations.
// State is in-memory only; we deliberately do NOT persist to localStorage so the
// cart is cleared on every page reload (avoids stale carts the morning of the festival).

import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';

const ReservationCartContext = createContext(null);

// item shape: { brand, expression, price, vip }
// key shape:  `${brand}::${expression}`

const keyOf = (brand, expression) => `${brand}::${expression}`;

export const ReservationCartProvider = ({ children }) => {
  // Map<string, { brand, expression, price, vip, quantity }>
  const [items, setItems] = useState(() => new Map());
  const [isCheckoutOpen, setCheckoutOpen] = useState(false);

  const addItem = useCallback(({ brand, expression, price, vip }) => {
    setItems((prev) => {
      const next = new Map(prev);
      const k = keyOf(brand, expression);
      const existing = next.get(k);
      next.set(k, {
        brand, expression, price, vip,
        quantity: existing ? existing.quantity + 1 : 1,
      });
      return next;
    });
  }, []);

  const setQuantity = useCallback((brand, expression, qty) => {
    setItems((prev) => {
      const next = new Map(prev);
      const k = keyOf(brand, expression);
      if (qty <= 0) { next.delete(k); return next; }
      const existing = next.get(k);
      if (existing) next.set(k, { ...existing, quantity: Math.min(qty, 24) });
      return next;
    });
  }, []);

  const removeItem = useCallback((brand, expression) => {
    setItems((prev) => {
      const next = new Map(prev);
      next.delete(keyOf(brand, expression));
      return next;
    });
  }, []);

  const clear = useCallback(() => setItems(new Map()), []);

  const value = useMemo(() => {
    const list = Array.from(items.values());
    const totalQty = list.reduce((a, i) => a + i.quantity, 0);
    const subtotal = list.reduce((a, i) => a + (i.price || 0) * i.quantity, 0);
    const has = (brand, expression) => items.has(keyOf(brand, expression));
    return {
      items: list, totalQty, subtotal, has,
      addItem, setQuantity, removeItem, clear,
      isCheckoutOpen, openCheckout: () => setCheckoutOpen(true), closeCheckout: () => setCheckoutOpen(false),
    };
  }, [items, addItem, setQuantity, removeItem, clear, isCheckoutOpen]);

  return (
    <ReservationCartContext.Provider value={value}>
      {children}
    </ReservationCartContext.Provider>
  );
};

export const useReservationCart = () => {
  const ctx = useContext(ReservationCartContext);
  if (!ctx) throw new Error('useReservationCart must be used inside ReservationCartProvider');
  return ctx;
};
